import { useCallback, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { changeLanguage, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/i18n/config';

const LANGUAGE_STORAGE_KEY = '@app_language_preference';

export function useLanguage() {
  const { i18n, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string | 'default'>('default');
  const [isLoading, setIsLoading] = useState(true);

  const supportedLanguages = useMemo(
    () => ({
      default: t('settings.system'),
      ...SUPPORTED_LANGUAGES,
    }),
    [i18n.language, t]
  );

  // Load saved language preference on mount
  useEffect(() => {
    const loadSavedLanguage = async () => {
      try {
        const savedLanguage = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (savedLanguage) {
          setSelectedLanguage(savedLanguage as 'default' | string);
          if (savedLanguage !== 'default') {
            changeLanguage(savedLanguage);
          }
        }
      } catch (error) {
        console.error('Failed to load language preference:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedLanguage();
  }, []);

  // Function to update language
  const updateLanguage = useCallback(async (language: string | 'default') => {
    try {
      await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      setSelectedLanguage(language);
      changeLanguage(language);
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  }, []);

  // Return current device language
  const getCurrentDeviceLanguage = () => {
    return DEFAULT_LANGUAGE;
  };

  // Get display name for current language
  const getCurrentLanguageDisplay = (): string => {
    if (selectedLanguage === 'default') {
      return supportedLanguages.default;
    }
    return (
      supportedLanguages[selectedLanguage as keyof typeof supportedLanguages] || selectedLanguage
    );
  };

  return {
    selectedLanguage,
    updateLanguage,
    getCurrentDeviceLanguage,
    getCurrentLanguageDisplay,
    supportedLanguages,
    isLoading,
    currentLanguage: i18n.language
  };
}
